"use client";
import { useState, useEffect } from "react";
import {
  Button,
  Avatar,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@nextui-org/react";

const AuthorModal = ({
  isOpen,
  onOpenChange,
  authors,
  handleClickVerificateButton,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPagePublications, setCurrentPagePublications] = useState(1);
  const [currentPagePatents, setCurrentPagePatents] = useState(1);
  const [currentPageProjects, setCurrentPageProjects] = useState(1);
  const itemsPerPage = 2;

  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      setCurrentPagePublications(1);
      setCurrentPagePatents(1);
      setCurrentPageProjects(1);
    }
  }, [isOpen]);

  const totalPages = (items, itemsPerPage) => Math.ceil(items / itemsPerPage);

  const handleNextPage = (type) => {
    if (type === "authors" && currentPage < authors.length) {
      setCurrentPage(currentPage + 1);
      setCurrentPagePublications(1);
      setCurrentPagePatents(1);
      setCurrentPageProjects(1);
    } else {
      const pages = {
        publications: () => {
          if (
            currentPagePublications <
            totalPages(currentAuthor.documents.length, itemsPerPage)
          ) {
            setCurrentPagePublications(currentPagePublications + 1);
          }
        },
        patents: () => {
          if (
            currentPagePatents <
            totalPages(currentAuthor.patents.length, itemsPerPage)
          ) {
            setCurrentPagePatents(currentPagePatents + 1);
          }
        },
        projects: () => {
          if (
            currentPageProjects <
            totalPages(currentAuthor.projects.length, itemsPerPage)
          ) {
            setCurrentPageProjects(currentPageProjects + 1);
          }
        },
      };
      if (type in pages) pages[type]();
    }
  };

  const handlePreviousPage = (type) => {
    if (type === "authors" && currentPage > 1) {
      setCurrentPage(currentPage - 1);
      setCurrentPagePublications(1);
      setCurrentPagePatents(1);
      setCurrentPageProjects(1);
    } else {
      const pages = {
        publications: () => {
          if (currentPagePublications > 1) {
            setCurrentPagePublications(currentPagePublications - 1);
          }
        },
        patents: () => {
          if (currentPagePatents > 1) {
            setCurrentPagePatents(currentPagePatents - 1);
          }
        },
        projects: () => {
          if (currentPageProjects > 1) {
            setCurrentPageProjects(currentPageProjects - 1);
          }
        },
      };
      if (type in pages) pages[type]();
    }
  };

  const currentAuthor =
    authors && authors.length > 0 ? authors[currentPage - 1] : null;
  const currentDocuments = currentAuthor
    ? currentAuthor.documents.slice(
        (currentPagePublications - 1) * itemsPerPage,
        currentPagePublications * itemsPerPage
      )
    : [];
  const currentPatents = currentAuthor
    ? currentAuthor.patents.slice(
        (currentPagePatents - 1) * itemsPerPage,
        currentPagePatents * itemsPerPage
      )
    : [];
  const currentProjects = currentAuthor
    ? currentAuthor.projects.slice(
        (currentPageProjects - 1) * itemsPerPage,
        currentPageProjects * itemsPerPage
      )
    : [];

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      className="max-w-4xl mx-auto"
    >
      <ModalContent>
        <ModalHeader className="text-lg">
          Confirmación de Autenticidad y Asociatividad del Perfil
        </ModalHeader>
        <ModalBody className="text-sm">
          {authors && authors.length > 1 && (
            <div className="flex justify-between items-center mb-4">
              <Button
                disabled={currentPage === 1}
                onPress={() => handlePreviousPage("authors")}
                auto
              >
                Autor Anterior
              </Button>
              <span>
                Página {currentPage} de {authors.length}
              </span>
              <Button
                disabled={currentPage === authors.length}
                onPress={() => handleNextPage("authors")}
                auto
              >
                Autor Siguiente
              </Button>
            </div>
          )}
          {currentAuthor ? (
            <>
              <strong className="text-base">Nombre:</strong>
              <div className="flex items-center space-x-4 mt-2 mb-4">
                {currentAuthor.image_1 ? (
                  <Avatar
                    src={currentAuthor.image_1}
                    className="w-16 h-16"
                    name={`Foto de ${currentAuthor.label}`}
                  />
                ) : (
                  <Avatar
                    src="/autoricon.png"
                    className="w-16 h-16"
                    name={`Foto de ${currentAuthor.label}`}
                  />
                )}
                <span className="text-lg font-medium">
                  {currentAuthor.label}
                </span>
              </div>
              {currentAuthor.bio && (
                <>
                  <strong className="text-base">Biografía:</strong>{" "}
                  {currentAuthor.bio || "No disponible"}
                  <br />
                </>
              )}
              {currentDocuments.length > 0 && (
                <>
                  <strong className="text-base">Publicaciones:</strong>
                  <ul className="mt-2 space-y-2">
                    {currentDocuments.map((doc, index) => (
                      <li
                        key={index}
                        className="p-4 border rounded shadow-sm hover:bg-gray-100 transition-colors duration-200"
                      >
                        <p className="text-base font-medium">{doc.title}</p>
                        <p className="text-sm text-gray-500">
                          {doc.publicationYear}
                        </p>
                      </li>
                    ))}
                  </ul>
                  {totalPages(currentAuthor.documents.length, itemsPerPage) >
                    1 && (
                    <div className="flex justify-between items-center mt-4">
                      <Button
                        disabled={currentPagePublications === 1}
                        onPress={() => handlePreviousPage("publications")}
                        auto
                      >
                        Anterior
                      </Button>
                      <span>
                        Página {currentPagePublications} de{" "}
                        {totalPages(
                          currentAuthor.documents.length,
                          itemsPerPage
                        )}
                      </span>
                      <Button
                        disabled={
                          currentPagePublications * itemsPerPage >=
                          currentAuthor.documents.length
                        }
                        onPress={() => handleNextPage("publications")}
                        auto
                      >
                        Siguiente
                      </Button>
                    </div>
                  )}
                </>
              )}
              {currentPatents.length > 0 && (
                <>
                  <strong className="text-base">Patentes:</strong>
                  <ul className="mt-2 space-y-2">
                    {currentPatents.map((pat, index) => (
                      <li
                        key={index}
                        className="p-4 border rounded shadow-sm hover:bg-gray-100 transition-colors duration-200"
                      >
                        <p className="text-base font-medium">{pat.title}</p>
                        <p className="text-sm text-gray-500">
                          Registro: {pat.registrationDate} - Aprobación:{" "}
                          {pat.approvalDate}
                        </p>
                      </li>
                    ))}
                  </ul>
                  {totalPages(currentAuthor.patents.length, itemsPerPage) >
                    1 && (
                    <div className="flex justify-between items-center mt-4">
                      <Button
                        disabled={currentPagePatents === 1}
                        onPress={() => handlePreviousPage("patents")}
                        auto
                      >
                        Anterior
                      </Button>
                      <span>
                        Página {currentPagePatents} de{" "}
                        {totalPages(currentAuthor.patents.length, itemsPerPage)}
                      </span>
                      <Button
                        disabled={
                          currentPagePatents * itemsPerPage >=
                          currentAuthor.patents.length
                        }
                        onPress={() => handleNextPage("patents")}
                        auto
                      >
                        Siguiente
                      </Button>
                    </div>
                  )}
                </>
              )}
              {currentProjects.length > 0 && (
                <>
                  <strong className="text-base">Proyectos:</strong>
                  <ul className="mt-2 space-y-2">
                    {currentProjects.map((proj, index) => (
                      <li
                        key={index}
                        className="p-4 border rounded shadow-sm hover:bg-gray-100 transition-colors duration-200"
                      >
                        <p className="text-base font-medium">{proj.title}</p>
                        <p className="text-sm text-gray-500">
                          Inicio: {proj.startDate} - Fin: {proj.endDate}
                        </p>
                      </li>
                    ))}
                  </ul>
                  {totalPages(currentAuthor.projects.length, itemsPerPage) >
                    1 && (
                    <div className="flex justify-between items-center mt-4">
                      <Button
                        disabled={currentPageProjects === 1}
                        onPress={() => handlePreviousPage("projects")}
                        auto
                      >
                        Anterior
                      </Button>
                      <span>
                        Página {currentPageProjects} de{" "}
                        {totalPages(
                          currentAuthor.projects.length,
                          itemsPerPage
                        )}
                      </span>
                      <Button
                        disabled={
                          currentPageProjects * itemsPerPage >=
                          currentAuthor.projects.length
                        }
                        onPress={() => handleNextPage("projects")}
                        auto
                      >
                        Siguiente
                      </Button>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <p>Cargando datos del autor...</p>
          )}
        </ModalBody>
        <ModalFooter className="text-sm">
          <div className="text-red-600 text-xs mb-2 text-justify">
            Al aceptar, usted confirma que es el autor de toda la información
            mostrada y que los datos corresponden a su persona. Una vez
            aceptado, no será posible desasociar el perfil de manera automática.
            Si desea desasociarlo, deberá seguir un proceso manual que será
            realizado por el personal de CEDIA, y deberá notificarlo usted
            personalmente a &quot;noc@cedia.org.ec&quot;.
          </div>
          <Button
            color="danger"
            variant="light"
            onPress={() => onOpenChange(false)}
          >
            Cerrar
          </Button>
          <Button color="primary" onPress={handleClickVerificateButton}>
            Aceptar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AuthorModal;
